import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Home, Building2, Users, Baby, MoreHorizontal,
  Bug, Megaphone, LogOut
} from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { useTheme } from '@/lib/ThemeContext';

const NAV_ITEMS = [
  { name: 'Start', icon: Home, page: '/PlatformDashboard' },
  { name: 'Placówki', icon: Building2, page: '/Tenants' },
  { name: 'Admini', icon: Users, page: '/Administrators' },
  { name: 'Zgłoszenia', icon: Bug, page: '/BugReports' },
];

const MORE_ITEMS = [
  { name: 'Ogłoszenia', icon: Megaphone, page: '/SystemAnnouncements' },
  { name: 'Osierocone', icon: Baby, page: '/OrphanedRecords' },
];

export default function SuperAdminBottomNav({ onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const { getActiveColors } = useTheme();
  const themeColors = getActiveColors();

  const isActive = (page) => {
    return location.pathname === page || location.pathname === page.toLowerCase();
  };

  const handleNavClick = (page) => {
    navigate(page);
  };

  const handleMoreItemClick = (page) => {
    setMoreOpen(false);
    navigate(page);
  };

  return (
    <>
      {/* Bottom Navigation Bar - Uses theme colors */}
      <nav className={cn("fixed bottom-0 left-0 right-0 z-50 safe-area-bottom", themeColors.headerBg)}>
        <div className="flex items-center justify-around h-16 px-2">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.page);
            return (
              <button
                key={item.page}
                onClick={() => handleNavClick(item.page)}
                className={cn(
                  "flex flex-col items-center justify-center flex-1 h-full gap-1 transition-colors",
                  active ? "text-white" : "text-white/70 hover:text-white"
                )}
              >
                <Icon className="w-5 h-5" />
                <span className={cn(
                  "text-xs",
                  active ? "font-semibold" : "font-medium"
                )}>
                  {item.name}
                </span>
              </button>
            );
          })}

          {/* More Button */}
          <button
            onClick={() => setMoreOpen(true)}
            className={cn(
              "flex flex-col items-center justify-center flex-1 h-full gap-1 transition-colors",
              moreOpen ? "text-white" : "text-white/70 hover:text-white"
            )}
          >
            <MoreHorizontal className="w-5 h-5" />
            <span className="text-xs font-medium">Inne</span>
          </button>
        </div>
      </nav>

      {/* More Sheet (Bottom Drawer) */}
      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl">
          <SheetHeader className="pb-4">
            <SheetTitle>Inne opcje</SheetTitle>
          </SheetHeader>

          <div className="space-y-2 pb-8">
            {MORE_ITEMS.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.page);
              return (
                <button
                  key={item.page}
                  onClick={() => handleMoreItemClick(item.page)}
                  className={cn(
                    "flex items-center gap-4 w-full p-4 rounded-xl transition-colors",
                    active
                      ? "bg-crimson-50 text-crimson-600"
                      : "hover:bg-slate-50 text-slate-700"
                  )}
                >
                  <div className={cn(
                    "p-2 rounded-lg",
                    active ? "bg-crimson-100" : "bg-slate-100"
                  )}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <span className={cn(
                    "font-medium",
                    active && "font-semibold"
                  )}>
                    {item.name}
                  </span>
                </button>
              );
            })}

            {/* Divider */}
            <div className="border-t border-slate-200 my-4" />

            {/* Logout Button */}
            <button
              onClick={() => {
                setMoreOpen(false);
                onLogout?.();
              }}
              className="flex items-center gap-4 w-full p-4 rounded-xl transition-colors hover:bg-red-50 text-red-600"
            >
              <div className="p-2 rounded-lg bg-red-100">
                <LogOut className="w-5 h-5" />
              </div>
              <span className="font-medium">Wyloguj</span>
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
